import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import type { DataZoneLowerCaseString, QualityLevel, SelectedSortByValue } from '@/entities/quality';
import type { QualityDetailDateRange } from '@/entities/quality/domain/type';

export type AssetQualityTabValue = 'table' | 'column';

type AssetQualityFilterState = {
  selectedTableValue: 'All' | string;
  selectedColumnNameValue: 'All' | string;
  selectedDataZoneValue: 'All' | DataZoneLowerCaseString;
  selectedDataQualityValue: 'All' | QualityLevel;
  selectedDateRangeValue: QualityDetailDateRange;
  selectedSortByValue: SelectedSortByValue;
};

type AssetQualityFilterStoreState = {
  table: AssetQualityFilterState;
  column: AssetQualityFilterState;
};

type AssetQualityFilterActions = {
  setSelectedTableValue: (tabValue: AssetQualityTabValue, value: 'All' | string) => void;
  setSelectedColumnNameValue: (tabValue: AssetQualityTabValue, value: 'All' | string) => void;
  setSelectedDataZoneValue: (tabValue: AssetQualityTabValue, value: 'All' | DataZoneLowerCaseString) => void;
  setSelectedDataQualityValue: (tabValue: AssetQualityTabValue, value: 'All' | QualityLevel) => void;
  setSelectedDateRangeValue: (tabValue: AssetQualityTabValue, value: QualityDetailDateRange) => void;
  setSelectedSortByValue: (tabValue: AssetQualityTabValue, value: SelectedSortByValue) => void;
  clearAllFilters: (tabValue: AssetQualityTabValue) => void;
};

type AssetQualityFilterStore = AssetQualityFilterStoreState & AssetQualityFilterActions;

const INITIAL_TAB_STATE: AssetQualityFilterState = {
  selectedTableValue: 'All',
  selectedColumnNameValue: 'All',
  selectedDataZoneValue: 'All',
  selectedDataQualityValue: 'All',
  selectedDateRangeValue: 7,
  selectedSortByValue: '-collectedAt',
};

const INITIAL_STATE: AssetQualityFilterStoreState = {
  table: { ...INITIAL_TAB_STATE },
  column: { ...INITIAL_TAB_STATE },
};

export const useAssetQualityFilterStore = create<AssetQualityFilterStore>()(
  persist(
    (set) => ({
      ...INITIAL_STATE,
      setSelectedTableValue: (tabValue, value) =>
        set((state) => ({
          [tabValue]: { ...state[tabValue], selectedTableValue: value },
        })),
      setSelectedColumnNameValue: (tabValue, value) =>
        set((state) => ({
          [tabValue]: { ...state[tabValue], selectedColumnNameValue: value },
        })),
      setSelectedDataZoneValue: (tabValue, value) =>
        set((state) => ({
          [tabValue]: { ...state[tabValue], selectedDataZoneValue: value },
        })),
      setSelectedDataQualityValue: (tabValue, value) =>
        set((state) => ({
          [tabValue]: { ...state[tabValue], selectedDataQualityValue: value },
        })),
      setSelectedDateRangeValue: (tabValue, value) =>
        set((state) => ({
          [tabValue]: { ...state[tabValue], selectedDateRangeValue: value },
        })),
      setSelectedSortByValue: (tabValue, value) =>
        set((state) => ({
          [tabValue]: { ...state[tabValue], selectedSortByValue: value },
        })),
      clearAllFilters: (tabValue) =>
        set((state) => ({
          ...state,
          [tabValue]: { ...INITIAL_TAB_STATE },
        })),
    }),
    {
      name: 'asset-quality-filter',
      partialize: (state) => ({
        table: state.table,
        column: state.column,
      }),
    }
  )
);

export const assetQualityFilterStoreActions = {
  setSelectedTableValue: (tabValue: AssetQualityTabValue, value: 'All' | string) =>
    useAssetQualityFilterStore.getState().setSelectedTableValue(tabValue, value),
  setSelectedColumnNameValue: (tabValue: AssetQualityTabValue, value: 'All' | string) =>
    useAssetQualityFilterStore.getState().setSelectedColumnNameValue(tabValue, value),
  setSelectedDataZoneValue: (tabValue: AssetQualityTabValue, value: 'All' | DataZoneLowerCaseString) =>
    useAssetQualityFilterStore.getState().setSelectedDataZoneValue(tabValue, value),
  setSelectedDataQualityValue: (tabValue: AssetQualityTabValue, value: 'All' | QualityLevel) =>
    useAssetQualityFilterStore.getState().setSelectedDataQualityValue(tabValue, value),
  setSelectedDateRangeValue: (tabValue: AssetQualityTabValue, value: QualityDetailDateRange) =>
    useAssetQualityFilterStore.getState().setSelectedDateRangeValue(tabValue, value),
  setSelectedSortByValue: (tabValue: AssetQualityTabValue, value: SelectedSortByValue) =>
    useAssetQualityFilterStore.getState().setSelectedSortByValue(tabValue, value),
  clearAllFilters: (tabValue: AssetQualityTabValue) => useAssetQualityFilterStore.getState().clearAllFilters(tabValue),
};
